import cn from 'clsx'
import React, {type FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useFetcher, useRouteLoaderData} from '@remix-run/react'
import {getTranslation} from '~/localization/translation'
import type {ImprovedHomepageDataProps, RootLoaderResponse} from '~/types'
import {useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {IconChevronRightThick} from '~/icons/brg-icon-chevron-right-thick'
import {getDomainConfig} from '~/domains'
import TopListCard from './top-list-card'

interface TopListsProps {
  topLists: ImprovedHomepageDataProps['topLists']
}

const MIN_ANALYZED_QUANTITY = 12
const MAX_ANALYZED_QUANTITY = 80
const TOP_QUANTITY_ANALYZED = 10

function getDeterministicHash(input: string) {
  const hash = Array.from(input).reduce((accumulator, char) => {
    const nextHash = (accumulator << 5) - accumulator + char.charCodeAt(0)
    return nextHash | 0
  }, 0)

  return Math.abs(hash)
}

function getDailyAnalyzedQuantity(daySeed: string, listSlug: string) {
  const rangeSize = MAX_ANALYZED_QUANTITY - MIN_ANALYZED_QUANTITY + 1
  const normalizedValue = getDeterministicHash(`${daySeed}:${listSlug}`) % rangeSize
  return MIN_ANALYZED_QUANTITY + normalizedValue
}

const TopLists: FC<TopListsProps> = ({topLists}) => {
  const {locale, domain, language} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const {styles: domainStyles} = getDomainConfig(domain, locale)
  const topListFetcher = useFetcher<{topList: ImprovedHomepageDataProps['topLists'][number] | null}>()
  const {load: loadTopListsByCategory} = topListFetcher
  const [topListsState, setTopListsState] = useState(topLists)
  const [selectedCategory, setSelectedCategory] = useState(topLists[0]?.category ?? '')
  const [loadingCategory, setLoadingCategory] = useState<string | null>(null)
  const fetchedCategoriesRef = useRef(new Set<string>(topLists[0]?.category ? [topLists[0]?.category] : []))
  const inFlightCategoryRef = useRef<string | null>(null)
  const chipsContainerRef = useRef<HTMLDivElement | null>(null)
  const selectedChipRef = useRef<HTMLButtonElement | null>(null)
  const shouldAutoScrollSelectedChipRef = useRef(false)
  const reportWidgetClicked = useReportWidgetClick()
  const [sectionRef] = useReportWidgetSeen({
    widget_name: 'improved-home-top-lists-section',
    widget_place: 'top',
  })

  const selectedTopList = useMemo(
    () => topListsState.find((topList) => topList.category === selectedCategory) ?? topListsState[0],
    [selectedCategory, topListsState],
  )
  const selectedCategoryIndex = topListsState.findIndex((topList) => topList.category === selectedCategory)
  const canShowPrevCategory = selectedCategoryIndex > 0
  const canShowNextCategory = selectedCategoryIndex >= 0 && selectedCategoryIndex < topListsState.length - 1
  const daySeed = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const analyzedQtyBySlug = useMemo(
    () =>
      Object.fromEntries(
        (selectedTopList?.lists ?? []).map((list) => [list.slug, getDailyAnalyzedQuantity(daySeed, list.slug)]),
      ),
    [daySeed, selectedTopList?.lists],
  )

  useEffect(() => {
    setTopListsState(topLists)
    setSelectedCategory(topLists[0]?.category ?? '')
    setLoadingCategory(null)
    fetchedCategoriesRef.current = new Set(topLists[0]?.category ? [topLists[0]?.category] : [])
    inFlightCategoryRef.current = null
  }, [topLists])

  useEffect(() => {
    if (topListFetcher.state !== 'idle') return

    const inFlightCategory = inFlightCategoryRef.current
    if (!inFlightCategory) return

    const fetchedTopList = topListFetcher.data?.topList
    if (fetchedTopList) {
      setTopListsState((currentTopLists) =>
        currentTopLists.map((topList) =>
          topList.category === fetchedTopList.category ? {...topList, lists: fetchedTopList.lists} : topList,
        ),
      )
      fetchedCategoriesRef.current.add(fetchedTopList.category)
    } else {
      fetchedCategoriesRef.current.add(inFlightCategory)
    }

    if (loadingCategory === inFlightCategory) setLoadingCategory(null)
    inFlightCategoryRef.current = null
  }, [loadingCategory, topListFetcher.data, topListFetcher.state])

  const fetchCategoryLists = useCallback(
    (category: string, withLoadingState = false) => {
      if (!category || fetchedCategoriesRef.current.has(category)) return

      if (inFlightCategoryRef.current === category) {
        if (withLoadingState) setLoadingCategory(category)
        return
      }

      inFlightCategoryRef.current = category
      if (withLoadingState) setLoadingCategory(category)
      loadTopListsByCategory(`/improved-top-lists?category=${encodeURIComponent(category)}`)
    },
    [loadTopListsByCategory],
  )

  useEffect(() => {
    if (!selectedCategory || fetchedCategoriesRef.current.has(selectedCategory)) {
      if (loadingCategory === selectedCategory) setLoadingCategory(null)
      return
    }

    fetchCategoryLists(selectedCategory, true)
  }, [fetchCategoryLists, loadingCategory, selectedCategory])

  useEffect(() => {
    if (!shouldAutoScrollSelectedChipRef.current) return
    const chipsContainer = chipsContainerRef.current
    const selectedChip = selectedChipRef.current
    if (!chipsContainer || !selectedChip) return

    const containerRect = chipsContainer.getBoundingClientRect()
    const chipRect = selectedChip.getBoundingClientRect()
    const chipCenter = chipRect.left + chipRect.width / 2
    const containerCenter = containerRect.left + containerRect.width / 2
    const offsetFromCenter = chipCenter - containerCenter
    const nextScrollLeft = chipsContainer.scrollLeft + offsetFromCenter

    chipsContainer.scrollTo({
      left: Math.max(0, nextScrollLeft),
      behavior: 'smooth',
    })
    shouldAutoScrollSelectedChipRef.current = false
  }, [selectedCategory])

  const handleCategoryClick = (category: string, itemsCount: number) => {
    reportWidgetClicked({
      widget_name: 'improved-home-top-lists-category',
      widget_place: 'top',
      trigger_type: category,
      variation: JSON.stringify({
        itemsQuantity: itemsCount,
      }),
    })
    shouldAutoScrollSelectedChipRef.current = true
    fetchCategoryLists(category, true)
    setSelectedCategory(category)
  }

  const handleCategoryNavigation = (direction: 'prev' | 'next') => {
    if (selectedCategoryIndex < 0) return
    const nextIndex = direction === 'next' ? selectedCategoryIndex + 1 : selectedCategoryIndex - 1
    const nextCategory = topListsState[nextIndex]
    if (!nextCategory) return

    shouldAutoScrollSelectedChipRef.current = true
    fetchCategoryLists(nextCategory.category, true)
    setSelectedCategory(nextCategory.category)
  }

  const isSelectedCategoryLoading = !fetchedCategoriesRef.current.has(selectedCategory)
  const useFourColumnLayout = (selectedTopList?.lists?.length ?? 0) >= 4

  return (
    <section ref={sectionRef} className={cn(domainStyles.UTILS_MAIN_CONTAINER, 'mx-auto w-full')}>
      <div className="mt-2 flex flex-col gap-y-1">
        <span className="text-base font-semibold uppercase tracking-widest text-[#61C200] mobile:text-xs">
          {translations.ImprovedHomePage.TopLists.title}
        </span>
        <h2 className="text-[32px] font-extrabold text-[#363636] mobile:text-2xl">
          {translations.ImprovedHomePage.TopLists.ourTopLists}
        </h2>
        <p className="text-base font-light text-[#828282] mobile:text-sm">
          {translations.ImprovedHomePage.TopLists.byCategories}
        </p>
      </div>
      <div className="relative mt-6 mobile:mt-4">
        {topListsState.length > 1 ? (
          <div
            ref={chipsContainerRef}
            className="no-scrollbar flex gap-3 overflow-x-auto pb-2 mobile:!w-screen mobile:pr-4"
          >
            {topListsState.map((topList) => {
              const isActive = topList.category === selectedCategory
              return (
                <button
                  ref={isActive ? selectedChipRef : null}
                  key={topList.id}
                  type="button"
                  onMouseEnter={() => fetchCategoryLists(topList.category)}
                  onFocus={() => fetchCategoryLists(topList.category)}
                  onClick={() => handleCategoryClick(topList.category, topList?.lists?.length ?? 0)}
                  className={cn(
                    'flex h-[40px] items-center whitespace-nowrap rounded-full px-6 py-3 text-base font-semibold transition-colors',
                    isActive ? 'bg-[#575757] text-white' : 'bg-white text-black shadow-[0_2px_9px_rgba(0,0,0,0.12)]',
                  )}
                >
                  {topList.category}
                </button>
              )
            })}
          </div>
        ) : null}
        <div className="relative w-full">
          <div className="no-scrollbar mt-2 flex gap-10 overflow-x-hidden px-1 py-3 tablet:grid tablet:grid-cols-2 tablet:gap-2 tablet:overflow-visible">
            {(selectedTopList?.lists ?? []).map((list, index) => (
              <TopListCard
                key={`${selectedCategory}-${list.id}-${index}`}
                list={list}
                quantityAnalyzed={translations.ImprovedHomePage.TopLists.topQuantityAnalyzed(
                  TOP_QUANTITY_ANALYZED,
                  analyzedQtyBySlug[list.slug] ?? MIN_ANALYZED_QUANTITY,
                )}
                cardIndex={index}
                category={selectedCategory}
                isLoading={isSelectedCategoryLoading}
                useFourColumnLayout={useFourColumnLayout}
              />
            ))}
          </div>
          {canShowPrevCategory ? (
            <button
              type="button"
              aria-label="Show previous category"
              onClick={() => handleCategoryNavigation('prev')}
              className="absolute -left-10 top-1/2 z-10 hidden size-[30px] -translate-y-1/2 items-center justify-center rounded-full bg-[#4F4F4F] drop-shadow-md transition desktop:flex mobile:hidden"
            >
              <IconChevronRightThick fill="white" className="size-3 -rotate-180" />
            </button>
          ) : null}
          {canShowNextCategory ? (
            <button
              type="button"
              aria-label="Show next category"
              onClick={() => handleCategoryNavigation('next')}
              className="absolute -right-10 top-1/2 z-10 hidden size-[30px] -translate-y-1/2 items-center justify-center rounded-full bg-[#4F4F4F] drop-shadow-md transition desktop:flex mobile:hidden"
            >
              <IconChevronRightThick fill="white" className="size-3" />
            </button>
          ) : null}
        </div>
      </div>
    </section>
  )
}

export default TopLists
